import { Link, useParams } from "react-router-dom";
import { FlaskConical, ArrowLeft, Clock } from "lucide-react";

const LABS = {
  ldms: {
    code: "LDMS",
    title: "Laboratoire Discours, Médias et Société",
    eyebrow: "Laboratoire de recherche",
    banner: "/media/research-lab.jpg",
    color: "#0A2E52",
    intro:
      "Le Laboratoire Discours, Médias et Société analyse les relations entre les médias, les discours publics et les transformations sociopolitiques, en intégrant notamment les enjeux de genre, de culture et de participation citoyenne dans l'espace public.",
    axes: [
      "Discours publics et transformations sociopolitiques",
      "Genre, culture et participation citoyenne",
      "Analyse critique des médias et de l'espace public",
      "Médias et démocratie en République Démocratique du Congo",
    ],
  },
  lcot: {
    code: "LCOT",
    title: "Laboratoire Communication, Organisations et Transitions",
    eyebrow: "Laboratoire de recherche",
    banner: "/media/university-hall.jpg",
    color: "#D4AF37",
    intro:
      "Le Laboratoire Communication, Organisations et Transitions étudie les pratiques de communication stratégique au sein des organisations publiques et privées, en mettant l'accent sur les transformations institutionnelles, la gouvernance, le développement durable et la communication pour le changement social.",
    axes: [
      "Communication stratégique et gouvernance organisationnelle",
      "Transitions institutionnelles et transformations managériales",
      "Communication pour le développement durable",
      "Communication pour le changement social",
    ],
  },
  leihn: {
    code: "LEIHN",
    title: "Laboratoire Écosystèmes Informationnels et Humanités Numériques",
    eyebrow: "Laboratoire de recherche",
    banner: "/media/modern-library.jpg",
    color: "#144C86",
    intro:
      "Le Laboratoire Écosystèmes Informationnels et Humanités Numériques se consacre à l'étude des systèmes d'information, de la documentation, des technologies numériques et de l'innovation communicationnelle.",
    axes: [
      "Systèmes d'information et écosystèmes documentaires",
      "Humanités numériques et patrimoines connectés",
      "Technologies numériques et innovation communicationnelle",
      "Data, IA et transformations informationnelles",
    ],
  },
};

export default function LaboratoireCecom() {
  const { code } = useParams();
  const lab = LABS[code?.toLowerCase()];

  if (!lab) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="lab-not-found">
        <h1 className="text-2xl font-bold text-unisic-blue">Laboratoire introuvable</h1>
        <Link to="/directions/bibliotheques-musees/cecom" className="mt-6 inline-flex items-center gap-2 link-underline">
          <ArrowLeft className="w-4 h-4" /> Retour au CECOM
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`lab-${lab.code.toLowerCase()}`}>
      <section className="relative h-[42vh] min-h-[300px] overflow-hidden">
        <img src={lab.banner} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{lab.eyebrow}</div>
          <div className="mt-2 inline-flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-11 h-11 rounded-sm"
              style={{ background: lab.color, color: "#fff" }}
            >
              <FlaskConical className="w-5 h-5" />
            </span>
            <span className="text-white/85 text-sm font-mono tracking-widest">{lab.code}</span>
          </div>
          <h1 className="mt-3 text-white text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-4xl">
            {lab.title}
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="eyebrow eyebrow-dark">Présentation</div>
              <span className="divider-gold mt-3" />
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">{lab.intro}</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-unisic-blue">Axes de recherche</h2>
              <span className="divider-gold mt-3" />
              <ul className="mt-5 space-y-3">
                {lab.axes.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 text-sm leading-relaxed">
                    <span
                      className="inline-flex items-center justify-center w-6 h-6 rounded-sm text-[11px] font-bold text-white flex-shrink-0 mt-0.5"
                      style={{ background: lab.color }}
                    >
                      {i + 1}
                    </span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside
            className="paper-card p-6 h-fit border-l-4"
            style={{ borderColor: lab.color }}
            data-testid="lab-soon"
          >
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-unisic-gold font-semibold">
              <Clock className="w-3.5 h-3.5" /> Bientôt disponible
            </div>
            <h3 className="mt-3 text-lg font-bold text-unisic-blue">Contenus en préparation</h3>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Les publications, projets en cours, chercheurs associés et événements du laboratoire
              <strong> {lab.code}</strong> seront très prochainement mis en ligne sur cette page.
            </p>
            <Link
              to="/directions/bibliotheques-musees/cecom"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-unisic-blue hover:text-unisic-gold"
              data-testid="lab-back"
            >
              <ArrowLeft className="w-4 h-4" /> Retour au CECOM
            </Link>
          </aside>
        </div>
      </section>
    </div>
  );
}
